import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import {getBrandsWithoutTemporaryIds, getTemporaryBrandObject, getUniqueNameForNewBrand, movePrimaryLogoToStart} from '@Libraries/brands-library';
import {getUniqueString} from '@Utils/string.util';
import {getUserId} from '@Libraries/user.library';
import type {Brand, BrandAssetType, BrandKitExtractionSource, BrandLogo, BrandsAppState, SelectableState} from '@Components/mystuff-brands/brand-kits.types';
import {BrandsLoadingState, EMPTY_SELECTABLE_COLORS_STATE, SELECTABLE_LOGO_EMPTY} from '@Components/mystuff-brands/brand-kits.types';
import {brandsExtraReducers, resetActivityLogForBrand} from './brands-extra-reducers';

/** Brand Kits is login-gated, so this is only a type-level fallback. */
const NOT_LOGGED_IN_CREATOR_ID = -1;

const initialState = (): BrandsAppState => {
  return {
    brands: [],
    activeBrandId: null,
    businessProfileBrandId: null,
    brandContent: {},
    brandActivityMap: {},
    brandActivity: {},
    brandActivityDetails: {},
    brandsLoadingState: BrandsLoadingState.NOT_LOADED,
    isNewBrandBeingCreated: false,
    isBrandKitExtractionInProgress: false,
    extractionFeedbackSource: null,
    requestedEditorAssetType: null,
    brandIdBeingMadeDefault: null,
    isUserTeamAdmin: false,
    fonts: {},
    colors: {},
    logos: {},
    images: {},
    videos: {},
    templates: {},
    selectableState: {
      selectedColors: {
        ids: EMPTY_SELECTABLE_COLORS_STATE,
        defaultSelected: false,
      },
      selectedLogo: {
        id: SELECTABLE_LOGO_EMPTY,
        defaultSelected: false,
      },
    },
  };
};

const brandsSlice = createSlice({
  name: 'Brands',
  initialState,
  reducers: {
    removeAllTemporaryBrands: (state): void => {
      state.brands = getBrandsWithoutTemporaryIds(state.brands);
    },

    addBrand: (state, action: PayloadAction<Brand>): void => {
      state.brands.push(action.payload);
    },

    addBrandImage: (state, action: PayloadAction<BrandLogo>): void => {
      addBrandLogoInfoFromServerInStore(state, action.payload);
    },

    setBrandKitExtractionInProgress: (state, action: PayloadAction<boolean>): void => {
      state.isBrandKitExtractionInProgress = action.payload;
    },

    setBrandKitExtractionFeedbackSource: (state, action: PayloadAction<BrandKitExtractionSource | null>): void => {
      state.extractionFeedbackSource = action.payload;
    },

    /**
     * The Brand Kits page fills every kit's logos, colours and fonts once, for the kit pills' previews. A kit
     * picked from the row reloads them, so a teammate's later edits show as they did before the preview
     * existed; the ids stay meanwhile, so the cards and the checklist keep what they show until the rows land.
     */
    markBrandKitPreviewAssetsForRefetch: (state, action: PayloadAction<string>): void => {
      if (!(action.payload in state.brandContent)) {
        return;
      }

      const kitContent = state.brandContent[action.payload];
      [kitContent.logos, kitContent.colors, kitContent.fonts].forEach((slot) => {
        if (slot.loaded === BrandsLoadingState.LOADED) {
          slot.loaded = BrandsLoadingState.NOT_LOADED;
        }
      });
    },

    setRequestedEditorAssetType: (state, action: PayloadAction<BrandAssetType | null>): void => {
      state.requestedEditorAssetType = action.payload;
    },

    setBrandsLoadingState: (state, action: PayloadAction<BrandsLoadingState>): void => {
      state.brandsLoadingState = action.payload;
    },

    setSelectedColorsInSelectableState: (state, action: PayloadAction<SelectableState['selectedColors']>): void => {
      state.selectableState.selectedColors = action.payload;
    },

    setSelectedLogoInSelectableState: (state, action: PayloadAction<SelectableState['selectedLogo']>): void => {
      state.selectableState.selectedLogo = action.payload;
    },
  },

  extraReducers: brandsExtraReducers,
});

const addBrandLogoInfoFromServerInStore = (state: BrandsAppState, ajaxResponse: BrandLogo): void => {
  state.brandContent[ajaxResponse.brandId].logos.ids.push(ajaxResponse.idbrandImage);
  state.logos[ajaxResponse.idbrandImage] = ajaxResponse;
  movePrimaryLogoToStart(state, ajaxResponse.brandId);
  resetActivityLogForBrand(state, ajaxResponse.brandId);
};

/** Renders a placeholder kit immediately, before the server has assigned a real brandId. */
export const addTemporaryBrandToState = (brands: Brand[]): Brand => {
  const newName = getUniqueNameForNewBrand(brands);
  const newBrandObject = getTemporaryBrandObject(getUniqueString(), newName, getUserId() ?? NOT_LOGGED_IN_CREATOR_ID);

  window.PMW.redux.store.dispatch(addBrand(newBrandObject));

  return newBrandObject;
};

export const {
  addBrand,
  removeAllTemporaryBrands,
  addBrandImage,
  setBrandsLoadingState,
  setSelectedColorsInSelectableState,
  setSelectedLogoInSelectableState,
  setBrandKitExtractionInProgress,
  setBrandKitExtractionFeedbackSource,
  setRequestedEditorAssetType,
  markBrandKitPreviewAssetsForRefetch,
} = brandsSlice.actions;

export const brandsReducer = brandsSlice.reducer;
